import React, { createContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { CartAbandonmentHeartbeatAPI } from "../infrastructure/CartAbandonmentHeartbeatAPI";
import { PropertyDataFetcher } from "../infrastructure/PropertyDataFetcher";
import { Heartbeat } from "../domain/Heartbeat";
import { LoyaltySession } from "../domain/LoyaltySession";
import { Storage } from "../domain/Storage";
import { Url } from "../domain/Url";
import { getLinkToLoyaltyLoginWithSessionId } from "../../../utils/misc";
import constants from "../../../utils/constants";
import { useInterval } from "./useInterval";
import { BookingCart } from "../domain/BookingCart";

const CartAbandonmentContext = createContext(null);

const CartAbandonmentContextProvider = ({ session, propertyTicker, children }) => {
  const HEARTBEAT_INTERVAL = 10000;
  const HEARTBEAT_ORIGIN = "abandonment_cart";
  const [configuration, setConfiguration] = useState(null);
  const intl = useIntl();

  const getCurrentLocale = () => intl.locale;

  const setCartAbandonmentLoyaltyData = data => {
    Storage.saveOnSessionStorage(constants.LOCAL_STORAGE_KEYS.HEARTBEAT_LOYALTY_DATA, data);
  };

  const setPropertyConfiguration = data => {
    setConfiguration(data);
  };

  const saveLastBookingCartInformation = data => {
    Storage.saveOnLocalStorage(
      constants.LOCAL_STORAGE_KEYS.HEARTBEAT_LAST_BOOKING_CART_DATA,
      data.bookingCart
    );
  };

  const getHeartbeatData = () => {
    const loyaltyData = Heartbeat.getLoyaltyStoredData();
    const queryParams = Url.getQueryParams();
    return Heartbeat.toDto(getCurrentLocale(), loyaltyData, queryParams);
  };

  const isValidToSendData = () => {
    return Heartbeat.isValidToSendHeartbeat(getHeartbeatData());
  };

  const isPropertyConfigurationCartAbandonmentActive = () => {
    return configuration ? configuration.get("activeAbandonmentCart") : false;
  };

  const hasSessionOnQueryParams = () => {
    return Url.hasQueryParam("session");
  };

  const sendBeat = async () => {
    console.info("heartbeating...");
    if (isPropertyConfigurationCartAbandonmentActive() && isValidToSendData()) {
      const data = getHeartbeatData();
      await CartAbandonmentHeartbeatAPI.sendHeartbeat(data);
      saveLastBookingCartInformation(data);
      console.info("heartbeat sent");
    }
  };

  const checkIfNeedToLoyaltyLogin = async (propertyTicker, session) => {
    const locale = getCurrentLocale();
    const loyaltySession = LoyaltySession.of(session);
    if (loyaltySession.hasCartAbandonmentOrigin()) {
      const bookingCart = BookingCart.saveReservations(loyaltySession.cartData);
      saveLastBookingCartInformation({ bookingCart });
    }
    const isFromCartAbandonmentEmail = hasSessionOnQueryParams();
    const isCartAbandonmentSession = loyaltySession.isCartAbandonmentSession();
    if (isFromCartAbandonmentEmail && isCartAbandonmentSession) {
      const propertyData = await PropertyDataFetcher.fetch(propertyTicker, locale, session.id);
      const loyaltyLoginUrl = getLinkToLoyaltyLoginWithSessionId({
        sessionId: session.id,
        propertyTicker,
        locale,
        brand: propertyData.brand,
        customerId: loyaltySession.guestData.customerId,
        origin: HEARTBEAT_ORIGIN,
      });
      window.location.replace(loyaltyLoginUrl);
    }
  };

  useEffect(() => {
    session &&
      checkIfNeedToLoyaltyLogin(propertyTicker, session).then(() =>
        console.info("loyalty login checked")
      );
  }, [propertyTicker, session, intl]);

  useInterval(async () => await sendBeat(), HEARTBEAT_INTERVAL, [configuration, intl]);

  return (
    <CartAbandonmentContext.Provider
      value={{ setCartAbandonmentLoyaltyData, setPropertyConfiguration }}>
      {children}
    </CartAbandonmentContext.Provider>
  );
};

export { CartAbandonmentContext, CartAbandonmentContextProvider };
